/**
 * The replay's side of reactions, the parts that are plain maths
 * (`prototype/live-sessions.html`, screens 5 and 6): the strip over the bar,
 * the live's reactions rising again at their second, and the taps a replay
 * page gathers to send in batches.
 */
import {
  type ReactionKind,
  type ReactionTimeline,
  REPLAY_BATCH_MAX,
  type ReplayReactionsRequest,
} from "../../shared/live-reactions";
import { addCounts, byCount } from "../live/room/reactions";

export type ReplayTap = ReplayReactionsRequest["taps"][number];

/** A moment makes sense when you hear it land, not from its last word, so a mark plays from this much before. */
export const PLAY_BEFORE_SEC = 4;
/** How many busiest moments carry a mark on the bar. */
export const MARKS = 3;
/** The live's reactions rise again on the replay, quieter than the live: at most this many a second. */
export const ECHO_PER_SEC = 2;

// ── the strip over the bar ──────────────────────────────────────────────
export interface Strip {
  /** The SVG's width in bars: the talk's length, so each bar sits over the seconds it counts. */
  width: number;
  /** Path data in a box `width` wide and 100 high: the live's bars, and the replay's on top of them. */
  live: string;
  replay: string;
}

/**
 * The strip: one bar per `group` bins (a phone's bar is narrow, so it joins
 * two), the live's reactions solid and the replay's stacked on top of them.
 * A bar grows slower than its count, so a quiet stretch still shows beside
 * the busiest moment.
 */
export function stripPaths(
  live: number[],
  replay: number[],
  opts: { bin: number; lengthSec: number; group?: number },
): Strip {
  const group = Math.max(1, Math.floor(opts.group ?? 1));
  const n = Math.ceil(Math.max(live.length, replay.length) / group);
  const L: number[] = [];
  const R: number[] = [];
  for (let i = 0; i < n; i++) {
    let a = 0;
    let b = 0;
    for (let j = i * group; j < (i + 1) * group; j++) {
      a += live[j] ?? 0;
      b += replay[j] ?? 0;
    }
    L.push(a);
    R.push(b);
  }
  const max = Math.max(1, ...L.map((a, i) => a + R[i]));
  let pl = "";
  let pr = "";
  L.forEach((a, i) => {
    const v = a + R[i];
    if (!v) return;
    const h = 100 * (v / max) ** 0.7;
    const hl = (h * a) / v;
    const hr = h - hl;
    const x = (i + 0.14).toFixed(2);
    if (hl > 0.05) pl += `M${x} 100h.72v${(-hl).toFixed(2)}h-.72z`;
    if (hr > 0.05) pr += `M${x} ${(100 - hl).toFixed(2)}h.72v${(-hr).toFixed(2)}h-.72z`;
  });
  const width = opts.lengthSec > 0 ? opts.lengthSec / (opts.bin * group) : Math.max(1, n);
  return { width, live: pl, replay: pr };
}

/** The bin a second falls in. */
export const binAt = (sec: number, timeline: Pick<ReactionTimeline, "bin">): number =>
  Math.max(0, Math.floor(sec / timeline.bin));

// ── the live's reactions, again ─────────────────────────────────────────
/**
 * How many of the live's reactions rise again at one second of the replay:
 * two where the live was busy, now and then one where it was quiet. `roll`
 * is a random number from 0 to 1.
 */
export function echoesAt(timeline: ReactionTimeline, sec: number, roll: number): number {
  const perSec = (timeline.live[binAt(sec, timeline)] ?? 0) / timeline.bin;
  if (perSec >= 1.6) return ECHO_PER_SEC;
  return roll < perSec * 0.6 ? 1 : 0;
}

/**
 * Which reaction rises again at a second. The timeline counts taps, not
 * kinds, so a second near one of the busiest moments takes that moment's
 * mix, and any other takes the mix of all of them. `roll` picks within it.
 */
export function echoKind(timeline: ReactionTimeline, sec: number, roll: number): ReactionKind {
  const near = timeline.moments.find((m) => Math.abs(m.at - sec) <= timeline.bin * 1.5);
  const mix = near ? near.live : timeline.moments.reduce((all, m) => addCounts(all, m.live), {});
  const ranked = byCount(mix);
  const total = ranked.reduce((sum, [, n]) => sum + n, 0);
  let left = roll * total;
  for (const [kind, n] of ranked) {
    left -= n;
    if (left < 0) return kind;
  }
  return ranked[0]?.[0] ?? "love";
}

// ── the taps made on the replay ─────────────────────────────────────────
/** The taps not yet sent, the oldest first. */
export class ReplayTaps {
  private queue: ReplayTap[] = [];

  add(tap: ReplayTap): void {
    this.queue.push(tap);
  }

  get size(): number {
    return this.queue.length;
  }

  /** The next batch, at most `REPLAY_BATCH_MAX`; the rest wait for the one after. */
  take(): ReplayTap[] {
    return this.queue.splice(0, REPLAY_BATCH_MAX);
  }
}
